import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import { useParams, Link } from 'react-router-dom';
import {
  Button,
  Flex,
  Icon,
  Input,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FaChevronRight, FaPlus } from 'react-icons/fa';

import { Provider } from '../../Chakra';
import { FETCH_COALITIONS, FETCH_EVENT_HEADER } from '../../GraphQL/Queries';
import useCurrentUser from '../../Hooks/userCurrentUser';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import { EVENT_TITLE } from '../../Constants/AppConstants';

import { CreateCoalitionModal } from './CreateCoalitionModal';

const coalitionPropType = PropTypes.objectOf({
  id: PropTypes.string.isRequired,
  code: PropTypes.string.isRequired,
  fetchEventCompetition: PropTypes.objectOf({
    title: PropTypes.string.isRequired,
  }).isRequired,
  fetchCoalitionsSchools: PropTypes.arrayOf({
    id: PropTypes.string.isRequired,
    fetchSchool: PropTypes.objectOf({
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
});

const stringMatches = (a, b) => a.toLowerCase().includes(b.toLowerCase());

const coalitionMatches = (coalition, search) => {
  const term = search.toLowerCase();
  return (
    stringMatches(coalition.code, term)
    || stringMatches(coalition.fetchEventCompetition.title, term)
    || coalition.fetchCoalitionsSchools.some((cs) => stringMatches(cs.fetchSchool.name, term))
  );
};

const CreateCoalitionButton = () => {
  const {
    isOpen,
    onOpen,
    onClose,
  } = useDisclosure();

  return (
    <>
      <Button
        variant="ghost"
        flexGrow={1}
        onClick={onOpen}
        leftIcon={<FaPlus />}
      >
        Create New Coalition
      </Button>
      {isOpen && (
        <CreateCoalitionModal isOpen={isOpen} onClose={onClose} />
      )}
    </>
  );
};

const CoalitionsPage = () => {
  const toast = useToast();
  const {
    currentUser,
    loading: userLoading,
  } = useCurrentUser(true);
  const { id } = useParams();

  const { data: eventData, loading: eventLoading } = useQuery(FETCH_EVENT_HEADER, {
    variables: {
      id,
    },
    skip: !currentUser,
  });
  const event = eventData?.fetchEventDetail ?? {};
  useHeaderComponentsMutation({
    title: 'COALITIONS',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const { data, loading: coalitionsLoading } = useQuery(
    FETCH_COALITIONS, {
      variables: {
        eventId: parseInt(id, 10),
      },
      skip: !currentUser,
      onError: (err) => {
        toast({
          title: err.message,
          status: 'error',
          isClosable: true,
          position: 'top',
        });
      },
    },
  );

  const [search, setSearch] = useState('');

  const sortedCoalitions = data?.fetchCoalitions.sort((a, b) => {
    if (a.fetchEventCompetition !== b.fetchEventCompetition) {
      return a.fetchEventCompetition.title.localeCompare(b.fetchEventCompetition.title);
    }
    return a.code.localeCompare(b.code);
  });

  const loading = userLoading || eventLoading || coalitionsLoading;

  if (loading) {
    return (
      <Spinner />
    );
  }

  return (
    <Flex direction="column" height="100%" width="100%" justifyContent="center" alignContent="center" padding={4} gap={2}>
      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="search for code, school, or competition..."
      />
      {sortedCoalitions
        .filter((coalition) => coalitionMatches(coalition, search))
        .map((coalition) => <CoalitionRow key={coalition.id} eventId={id} coalition={coalition} />)}
      <CreateCoalitionButton />
    </Flex>
  );
};

const CoalitionRow = ({ eventId, coalition }) => (
  <>
    <Link to={`/event-manage/${eventId}/coalitions/${coalition.id}/edit`}>
      <Flex paddingBlock={2} alignItems="center">
        <Flex direction="column" flexGrow={1}>
          <Text fontWeight="bold">
            Coalition
            {' '}
            {coalition.code}
          </Text>
          <Text>
            <Text fontWeight="bold" display="inline">
              {coalition.fetchEventCompetition.title}
              :
              {' '}
            </Text>
            {coalition.fetchCoalitionsSchools.map((cs) => cs.fetchSchool.name).join(', ')}
          </Text>
        </Flex>
        <Icon as={FaChevronRight} />
      </Flex>
    </Link>
    <hr />
  </>
);
CoalitionRow.propTypes = {
  eventId: PropTypes.string.isRequired,
  coalition: coalitionPropType.isRequired,
};


export const CoalitionsIndexPage = () => (
  <Provider>
    <Flex height="100%" width="100%" justifyContent="center" alignContent="center">
      <CoalitionsPage />
    </Flex>
  </Provider>
);
